/** UXP-00017 — Unified Commerce Platform framework (mirrors pkg/unifiedcommerce) */

export const UXP_UC_ID = 'UXP-00017';

export interface CommandCenterKPI {
  label: string;
  value: string;
  change: string;
  positive: boolean;
}

export interface KernelEntityRow {
  entity: string;
  records: number;
  synced: number;
}

export interface ConnectorRow {
  connector: string;
  status: string;
  health: number;
  lastSync: string;
}

export interface AdapterRow {
  feature: string;
  status: string;
  coverage: number;
}

export interface RuleRow {
  rule: string;
  marketplace: string;
  active: boolean;
  violations: number;
}

export interface ChannelMappingRow {
  type: string;
  source: string;
  mapped: number;
  pending: number;
}

export interface UnifiedOpRow {
  operation: string;
  status: string;
  records: number;
  channels: number;
}

export interface AdvisorRecRow {
  capability: string;
  title: string;
  detail: string;
  roi: string;
}

export const COMMAND_CENTER_METRICS = [
  'Connected Channels', 'Revenue by Channel', 'Profit by Channel', 'Marketplace Health',
  'Synchronization Health', 'API Health', 'Expansion Opportunities', 'Business KPIs',
];

export const KERNEL_ENTITIES = [
  'Products', 'Orders', 'Customers', 'Inventory', 'Warehouses', 'Settlements',
  'Campaigns', 'Promotions', 'Shipments', 'Invoices', 'Suppliers', 'Returns', 'Reviews', 'Payments',
];

export const MARKETPLACE_CONNECTORS = [
  'Amazon', 'Shopify', 'Flipkart', 'Myntra', 'AJIO', 'WooCommerce', 'Magento',
  'BigCommerce', 'eBay', 'Etsy', 'Future Marketplace SDK',
];

export const ADAPTER_FEATURES = [
  'Authentication', 'API Integration', 'Webhook Processing', 'Data Mapping', 'Validation',
  'Retry Logic', 'Rate-limit Handling', 'Health Monitoring', 'Version Management',
];

export const MARKETPLACE_RULES = [
  'Listing Rules', 'Image Rules', 'Pricing Rules', 'Inventory Rules', 'Shipping Rules',
  'Compliance Rules', 'Category Rules', 'Localization Rules',
];

export const CHANNEL_MAPPING_TYPES = [
  'Category Mapping', 'Brand Mapping', 'Attribute Mapping', 'Warehouse Mapping',
  'Tax Mapping', 'Pricing Mapping', 'Inventory Mapping',
];

export const UNIFIED_OPERATIONS = [
  'Unified Catalog', 'Unified Inventory', 'Unified Orders', 'Unified Advertising',
  'Unified Finance', 'Unified Customers',
];

export const LOCALIZATION_FEATURES = [
  'Languages', 'Currencies', 'Measurements', 'Regional SEO', 'Regional Compliance',
  'Regional Creative', 'Regional Content',
];

export const ADVISOR_CAPABILITIES = [
  'Expansion Strategy', 'Marketplace Selection', 'Localization', 'Pricing',
  'Inventory', 'Advertising', 'Expected ROI',
];

export const COMMERCE_ANALYTICS = [
  'Marketplace Growth', 'Connector Health', 'Localization Quality',
  'Expansion ROI', 'Cross-channel Performance',
];

export const COLLABORATION_FEATURES = [
  'Localization Reviews', 'Marketplace Approvals', 'Publishing', 'Assignments', 'Comments',
];

export const ACCESSIBILITY_STANDARDS = [
  'WCAG 2.2 AA', 'Keyboard Navigation', 'Screen Reader Support', 'Reduced Motion', 'Responsive Design',
];

export const ARCHITECTURE_STAGES = [
  'Unified Commerce Command Center', 'Commerce Kernel', 'Marketplace Connectors',
  'Marketplace Adapter Framework', 'Marketplace Rules', 'Channel Mapping',
  'Unified Operations', 'AI Marketplace Advisor',
];

export const LIVE_ROUTES: Record<string, string> = {
  amazonConnect: '/amazon/connect',
  marketplaceConnection: '/platform/marketplace-connection',
  integrations: '/integrations',
  catalog: '/amazon/catalog',
};

export const DEMO_KPIS: CommandCenterKPI[] = [
  { label: 'Connected Channels', value: '4 active', change: '+1 pending', positive: true },
  { label: 'Revenue by Channel', value: '₹48.2L', change: 'Amazon 62%', positive: true },
  { label: 'Profit by Channel', value: '₹14.6L', change: 'Shopify +22%', positive: true },
  { label: 'Marketplace Health', value: '96%', change: '+2%', positive: true },
  { label: 'Synchronization Health', value: '98.4%', change: '12 sync jobs', positive: true },
  { label: 'API Health', value: '99.1%', change: 'all green', positive: true },
  { label: 'Expansion Opportunities', value: '3 markets', change: 'Flipkart ready', positive: true },
  { label: 'Business KPIs', value: '91/100', change: '+4', positive: true },
];

export const DEMO_KERNEL: KernelEntityRow[] = [
  { entity: 'Products', records: 8420, synced: 8398 },
  { entity: 'Orders', records: 124000, synced: 124000 },
  { entity: 'Inventory', records: 68000, synced: 67920 },
  { entity: 'Customers', records: 42000, synced: 41980 },
  { entity: 'Settlements', records: 8400, synced: 8395 },
  { entity: 'Returns', records: 3200, synced: 3198 },
];

export const DEMO_CONNECTORS: ConnectorRow[] = [
  { connector: 'Amazon', status: 'connected', health: 98, lastSync: '2m ago' },
  { connector: 'Shopify', status: 'connected', health: 96, lastSync: '5m ago' },
  { connector: 'Flipkart', status: 'pending', health: 0, lastSync: '—' },
  { connector: 'WooCommerce', status: 'connected', health: 94, lastSync: '12m ago' },
];

export const DEMO_ADAPTERS: AdapterRow[] = [
  { feature: 'Authentication', status: 'active', coverage: 100 },
  { feature: 'Webhook Processing', status: 'active', coverage: 98 },
  { feature: 'Data Mapping', status: 'active', coverage: 96 },
  { feature: 'Rate-limit Handling', status: 'active', coverage: 100 },
  { feature: 'Health Monitoring', status: 'active', coverage: 100 },
];

export const DEMO_RULES: RuleRow[] = [
  { rule: 'Listing Rules', marketplace: 'Amazon.in', active: true, violations: 2 },
  { rule: 'Image Rules', marketplace: 'Amazon.in', active: true, violations: 0 },
  { rule: 'Pricing Rules', marketplace: 'All channels', active: true, violations: 1 },
  { rule: 'Compliance Rules', marketplace: 'Amazon.in', active: true, violations: 0 },
];

export const DEMO_MAPPINGS: ChannelMappingRow[] = [
  { type: 'Category Mapping', source: 'Amazon → Canonical', mapped: 842, pending: 12 },
  { type: 'Brand Mapping', source: 'Shopify → Canonical', mapped: 24, pending: 0 },
  { type: 'Warehouse Mapping', source: 'FBA → Canonical', mapped: 8, pending: 1 },
  { type: 'Tax Mapping', source: 'GST → Canonical', mapped: 18, pending: 0 },
];

export const DEMO_UNIFIED_OPS: UnifiedOpRow[] = [
  { operation: 'Unified Catalog', status: 'synced', records: 8420, channels: 3 },
  { operation: 'Unified Inventory', status: 'synced', records: 68000, channels: 3 },
  { operation: 'Unified Orders', status: 'live', records: 1240, channels: 3 },
  { operation: 'Unified Finance', status: 'synced', records: 8400, channels: 2 },
];

export const DEMO_ADVISOR: AdvisorRecRow[] = [
  { capability: 'Expansion Strategy', title: 'Launch Flipkart.in — Electronics category', detail: 'Predicted +₹18L/mo revenue · 82% category fit · inventory ready', roi: '3.2x' },
  { capability: 'Marketplace Selection', title: 'Prioritize Amazon.com over Myntra for Q3', detail: 'Combined forecast shows 2.4x ROI on .com vs fashion marketplace', roi: '2.4x' },
  { capability: 'Localization', title: 'Localize Hub Gen 3 listing for Amazon.com', detail: 'US market pricing + content adaptation · 76% confidence', roi: '1.8x' },
];

export function buildUnifiedCommerceDashboard() {
  return {
    command_center_metrics: COMMAND_CENTER_METRICS.length,
    kernel_entities: KERNEL_ENTITIES.length,
    marketplace_connectors: MARKETPLACE_CONNECTORS.length,
    adapter_features: ADAPTER_FEATURES.length,
    unified_operations: UNIFIED_OPERATIONS.length,
  };
}
